import { FC, useState } from 'react';

import {
  DELIVERY_CHANNEL_CONFIG,
  PendingChargeEstimateItem,
  PendingChargeEstimateResponse,
} from '@shared/api/giftCoupon/giftCoupon.interface';

/**
 * 가격 신뢰도 힌트.
 * - 최근 발송가라도 오래됐으면(8일+) 도매가 변동 가능성을 관리자가 감안하도록 표기
 * - 발송 이력이 없어 카탈로그 단가로 폴백한 경우/단가 미상도 구분 표기
 */
function estimateHint(item: PendingChargeEstimateItem): string | null {
  if (item.priceSource === 'recentDelivered') {
    if (!item.refDeliveredAt) return null;
    const days = Math.floor(
      (Date.now() - new Date(item.refDeliveredAt).getTime()) / 86_400_000,
    );
    return days >= 8 ? `${days}일 전 가격` : null;
  }
  if (item.priceSource === 'none') return '단가 미상';
  return '카탈로그 단가';
}

/**
 * 발송대기 매체별 충전 필요액 예상 칩 — 칩 클릭 시 상품별 내역 팝오버.
 * chargeEstimate가 null이면(구서버 404 등) 호출부에서 렌더하지 않는다.
 */
export const ChargeEstimateChips: FC<{ estimate: PendingChargeEstimateResponse }> = ({
  estimate,
}) => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const openBucket =
    estimate.channels.find((b) => (b.channel ?? 'null') === openKey) ?? null;
  const openLabel = openBucket
    ? openBucket.channel
      ? DELIVERY_CHANNEL_CONFIG[openBucket.channel].label
      : '미분류'
    : '';

  return (
    <div className="relative ml-auto flex flex-wrap items-center justify-end gap-1.5">
      <span
        className="text-xs text-gray-400"
        title="발송대기 전량 발송 시 매체별 예상 구매액 — 같은 상품의 최근 발송가 기준"
      >
        예상 발송대기 금액
      </span>

      {estimate.channels.map((bucket) => {
        const key = bucket.channel ?? 'null';
        const config = bucket.channel ? DELIVERY_CHANNEL_CONFIG[bucket.channel] : null;
        const isOpen = openKey === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => setOpenKey(isOpen ? null : key)}
            title={`${bucket.pendingCount}건 — 클릭하여 상품별 내역 보기`}
            className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
              config ? config.badgeClass : 'border-gray-300 bg-gray-50 text-gray-600'
            } ${isOpen ? 'ring-2 ring-indigo-300' : ''}`}
          >
            {config?.label ?? `미분류 ${bucket.pendingCount}건`}{' '}
            {bucket.estimatedAmount.toLocaleString()}원
          </button>
        );
      })}

      {openBucket && (
        <div className="absolute right-0 top-full z-20 mt-1.5 w-[360px] rounded-lg border bg-white text-left shadow-lg">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-xs font-semibold text-gray-700">
              {openLabel} — {openBucket.pendingCount}건 ·{' '}
              {openBucket.estimatedAmount.toLocaleString()}원
            </span>
            <button
              type="button"
              onClick={() => setOpenKey(null)}
              className="rounded px-1 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
          <div className="max-h-[240px] divide-y overflow-y-auto">
            {openBucket.items.map((item) => {
              const hint = estimateHint(item);
              return (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-2 px-3 py-1.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs text-gray-800" title={item.name}>
                      {item.name}
                    </p>
                    {hint && (
                      <p
                        className={`text-[10px] ${
                          item.priceSource === 'none' ? 'text-red-500' : 'text-amber-600'
                        }`}
                      >
                        {hint}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs font-medium text-gray-900">
                      {(item.count * item.unitPriceWhole).toLocaleString()}원
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {item.count}건 × {item.unitPriceWhole.toLocaleString()}원
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
